import { inject, Injector, runInInjectionContext } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, UrlTree } from '@angular/router';
import { MenuRole } from '@mkp/account/models';
import { selectWorkspaceFromRouteGuard } from '@mkp/auth/feature-guards';
import { composesGuards, GUARD_ROUND_TRIP_TIMEOUT_MS } from '@mkp/shared/util-guards';
import { Observable, of, switchMap } from 'rxjs';
import { permissionGuardFactory } from './permission.guard';

/**
 * Angular runs a canActivate array concurrently — runCanActivate ends in combineLatest, and
 * array order only decides which non-true result wins. Listing the workspace guard before the
 * permission guard therefore never expressed the dependency between them, even though the
 * permission check cannot dispatch until workspace selection has completed. Composing the two
 * puts them in sequence, which is what the routes meant all along.
 *
 * Sequencing them would otherwise stack their deadlines, so the second guard inherits what is
 * left of the first one's budget: the user waits once, not twice.
 */
export const workspacePermissionGuardFactory = (menuRole: MenuRole): CanActivateFn =>
  composesGuards(
    (route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> => {
      const injector = inject(Injector);
      const startedAt = Date.now();

      return selectWorkspaceFromRouteGuard(route).pipe(
        switchMap((workspaceResult) =>
          workspaceResult === true
            ? runInInjectionContext(
                injector,
                permissionGuardFactory(
                  menuRole,
                  Math.max(0, GUARD_ROUND_TRIP_TIMEOUT_MS - (Date.now() - startedAt))
                )
              )
            : of(workspaceResult)
        )
      );
    },
    [selectWorkspaceFromRouteGuard]
  );
